import React from 'react'

import { sortBy } from 'lodash'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { Item, Step } from 'modules/processes/domain'

interface ShowAlertUnsaved {
  steps: boolean
  items: boolean
}

interface StepsContextType {
  stepsHaveChanged: boolean
  itemsHaveChanged: boolean
  stepId: number | null
  steps: Step[]
  actionByResource: Record<string, Item[]>
  toggleUnsavedSearch: (key: keyof ShowAlertUnsaved) => void
  toggleSavedSearch: (key: keyof ShowAlertUnsaved) => void
  setSteps: React.Dispatch<React.SetStateAction<Step[]>>
  setStepId: React.Dispatch<React.SetStateAction<number | null>>
}

const StepsContext = React.createContext<StepsContextType | null>(null)

export const useSteps = () => {
  const SuperTasksModule = React.useContext(StepsContext)

  if (!SuperTasksModule) throw new Error('useSuperTasks must be used within an SuperTasksProvider')

  return SuperTasksModule
}

const initialStateAlert = { steps: false, items: false }

interface ProviderProps {
  initialState: Step[]
}

const actionByResource = (steps: Step[]) => {
  const filteredItems = steps
    .map((step) => step.items.filter((item) => item.configurationIdentifier))
    .flat()

  return filteredItems.reduce((acc: Record<string, Item[]>, obj) => {
    const { configurationIdentifier, ...rest } = obj

    if (!acc[String(configurationIdentifier)]) {
      acc[String(configurationIdentifier)] = []
    }
    acc[String(configurationIdentifier)]?.push(rest)

    return acc
  }, {})
}

export const StepsProvider = ({
  initialState,
  children,
}: React.PropsWithChildren<ProviderProps>) => {
  const sortSteps = () => {
    const sortedSteps = sortBy(initialState, (step) => Number(step.position))

    const isAnyStepPositionZero = sortedSteps.some((step) => step.position === 0)

    // Sort items within each step
    const sortedStepsWithItems = sortedSteps.map((step, stepIndex: number) => {
      const isAnyItemZero = step.items.some((item) => item.position === 0)

      const sortedItems = () => sortBy(step.items, (item) => Number(item.position))
      const itemsWithPosition = () =>
        step.items.map((item, itemIndex) => ({
          ...item,
          position: itemIndex + 1,
        }))

      return {
        ...step,
        position: isAnyStepPositionZero ? stepIndex + 1 : step.position,
        items: isAnyItemZero ? itemsWithPosition() : sortedItems(),
      }
    })

    return sortedStepsWithItems
  }

  const [state, setState] = React.useState<Step[]>(sortSteps)

  const [stepId, setStepId] = React.useState<number | null>(null)

  const [showAlertUnsaved, setShowAlertUnsaved] =
    React.useState<ShowAlertUnsaved>(initialStateAlert)

  const toggleUnsavedSearch = (key: keyof ShowAlertUnsaved) =>
    setShowAlertUnsaved((prev) => ({ ...prev, [key]: true }))

  const toggleSavedSearch = (key: keyof ShowAlertUnsaved) =>
    setShowAlertUnsaved((prev) => ({ ...prev, [key]: false }))

  useUnsafeEffect(() => {
    if (initialState) setState(sortSteps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState])

  return (
    <StepsContext.Provider
      value={{
        stepId: stepId,
        itemsHaveChanged: showAlertUnsaved.items,
        stepsHaveChanged: showAlertUnsaved.steps,
        steps: state,
        actionByResource: actionByResource(state),
        setStepId,
        toggleUnsavedSearch,
        toggleSavedSearch,
        setSteps: setState,
      }}
    >
      {children}
    </StepsContext.Provider>
  )
}
